import type {
  AveragePM,
  ChangeOverTime,
  DeviceActivations,
  IotCoreData,
  StdTelemetry,
  StdState,
  UploadStatus,
  UserCal,
  UserDeviceApiData
} from '@/types/devices'
import type { Study } from '@/types/studies'
import type {
  DeviceCoords,
  UnpackedDevice,
  FormattedMeasurement
} from '@/services/device-service/devices'
import type { TelemetryResult } from '@/services/device-metadata'
import type { TelemetryReading } from '@/helpers/devices/telemetryHelper'
import type { StatisticsReading } from '@/helpers/studies/statisticsHelper'
import type { DeviceDetails } from '@/helpers/devices/stateHelper'
import type { UserCalibration } from '@/services/device-calibration'

export interface LatestStudy {
  minTimestamp?: string
  maxTimestamp?: string
  duration?: number
  samplingInterval?: number
  uploadComplete?: boolean
  terminationReason?: string
  startTime?: string
  measurementStatistics?: Record<string, TelemetryReading | StatisticsReading> | null
}

export interface DevicesState {
  accountId: Record<string, string>
  coords: Record<string, DeviceCoords>
  model: Record<string, string>
  submodel: Record<string, string>
  serial: Record<string, string>
  indoor: Record<string, boolean>
  name: Record<string, string>
  locationName: Record<string, string>
  public: Record<string, boolean>
  shared: Record<string, boolean>
  privacyZone: Record<string, unknown>
  sensorReadings: Record<string, Record<string, FormattedMeasurement>>
  deviceStatus: Record<string, string[]>
  sensorStatus: Record<string, string[]>
  rssi: Record<string, number | null>
  connectionType: Record<string, string | null>
  averages: Record<string, AveragePM>
  percentChanged: Record<string, ChangeOverTime>
  lastCommunicationTime: Record<string, string | null>
  lastTelemetry: Record<string, string>
  lastState: Record<string, string | null>
  lastTelemetryStudyId: Record<string, string | null>
  latestStudy: Record<string, LatestStudy | null>
  firmware: Record<string, string | null>
  mqttHost: Record<string, string | null>
  sensorEoL: Record<string, boolean | null>
  userCals: Record<string, UserCalibration[]>
  zeroAppliedAt: Record<string, Record<string, string> | null>
  zeroState: Record<string, Record<string, string> | null>
  measurements: Record<string, Record<string, number | boolean | null>>
  allDevices: Record<string, UnpackedDevice>
  allUserDevices: Record<string, UserDeviceApiData>
  subscriptionMetadata: Record<string, DeviceActivations>
  subscriptionType: Record<string, string>
  connectedSmartBridgeModel: Record<string, string>
  connectedSmartBridgeSerial: Record<string, string | null>
  isConnected: Record<string, boolean>
  isTelemetry24HrsActive: Record<string, boolean>
  telemTxType: Record<string, number | null>
  uploadStatus: Record<string, UploadStatus>
  batteryCharging: Record<string, boolean | null>
  batteryInstalled: Record<string, boolean | null>
  batteryLow: Record<string, boolean | null>
  batteryRemaining: Record<string, number | null>
  calibrationDate: Record<string, string | null>
  flowCalibrationFactor: Record<string, number | null>
  userCal: Record<string, UserCal | null>
  errorState: Record<string, string[] | null>
  alarms: Record<string, string[] | null>
}

export interface SetDevicesPayload {
  [deviceId: string]: {
    accountId?: string
    coords?: DeviceCoords
    model?: string
    submodel?: string
    serial?: string
    indoor?: boolean
    name?: string
    locationName?: string
    isPublic?: boolean
    isShared?: boolean
    privacyZone?: unknown
    timestamp?: string
    readings?: Record<string, FormattedMeasurement>
    sensorStatus?: string[]
    deviceStatus?: string[]
  }
}

export interface SetUserDevicesPayload {
  [deviceId: string]: UserDeviceApiData
}

export interface SetDevicePayload {
  deviceId: string
  accountId?: string
  coords?: DeviceCoords
  model?: string
  submodel?: string
  serial?: string
  indoor?: boolean
  name?: string
  locationName?: string
  isPublic?: boolean
  isShared?: boolean
  privacyZone?: unknown
  timestamp?: string
  readings?: Record<string, FormattedMeasurement>
}

export interface SetSubscriptionMetadataPayload {
  deviceId: string
  activation?: DeviceActivations
}

export interface SetIotCoreDataPayload {
  deviceId: string
  iotCoreData: IotCoreData
}

export interface UpdateMetadataPayload {
  deviceId: string
  indoor: boolean
  name: string
  locationName: string
  model: string
  submodel: string
  serial: string
  isPublic: boolean
  isShared: boolean
}

export interface LatestTelemetryData extends TelemetryResult {
  deviceId: string
  readings?: StdTelemetry | null
}

export interface UpdateDeviceDetailsData {
  deviceId: string
  deviceState?: DeviceDetails | null
  studyState?: DeviceDetails | null
}

export interface UpdateStudyDetailsData {
  deviceId: string
  latest_device_study?: Study
  latest_study_state?: StdState
  latest_study_telemetry?: StdTelemetry
}

export enum DEVICE_MUTATIONS {
  SET_ALL_DEVICES = 'setAllDevices',
  SET_ALL_USER_DEVICES = 'setAllUserDevices',
  SET_DEVICES = 'setDevices',
  SET_USER_DEVICES = 'setUserDevices',
  SET_DEVICE = 'setDevice',
  UPDATE_COORDS = 'updateCoords',
  UPDATE_SENSOR_READINGS = 'updateSensorReadings',
  UPDATE_DEVICE_STATUS = 'updateDeviceStatus',
  UPDATE_SENSOR_STATUS = 'updateSensorStatus',
  UPDATE_RSSI = 'updateRSSI',
  UPDATE_CONNECTION_TYPE = 'updateConnectionType',
  UPDATE_EOL = 'updateEoL',
  UPDATE_USER_CALS = 'updateUserCals',
  UPDATE_FIRMWARE = 'updateFirmware',
  UPDATE_MQTT_HOST = 'updateMqttHost',
  UPDATE_CONNECTED_SMART_BRIDGE_SERIAL = 'updateConnectedSmartBridgeSerial',
  UPDATE_AVERAGES = 'updateAverages',
  UPDATE_PERCENT_CHANGED = 'updatePercentChanged',
  UPDATE_PUBLIC = 'updatePublic',
  UPDATE_PRIVACY_ZONE = 'updatePrivacyZone',
  UPDATE_LAST_TELEMETRY = 'updateLastTelemetry',
  UPDATE_LAST_STATE = 'updateLastState',
  UPDATE_LAST_TELEMETRY_STUDY_ID = 'updateLastTelemetryStudyId',
  UPDATE_LATEST_STUDY = 'updateLatestStudy',
  UPDATE_TELEM_TX_TYPE = 'updateTelemTxType',
  UPDATE_IS_CONNECTED = 'updateIsConnected',
  UPDATE_IS_TELEMETRY_24HRS_ACTIVE = 'updateIsTelemetry24HrsActive',
  UPDATE_INDOOR = 'updateIndoor',
  UPDATE_NAME = 'updateName',
  UPDATE_LOCATION_NAME = 'updateLocationName',
  UPDATE_MODEL = 'updateModel',
  UPDATE_ZERO_APPLIED_AT = 'updateZeroAppliedAt',
  UPDATE_ZERO_STATE = 'updateZeroState',
  UPDATE_SUBMODEL = 'updateSubmodel',
  UPDATE_SERIAL = 'updateSerial',
  UPDATE_SHARED = 'updateShared',
  UPDATE_CALIBRATION_DATE = 'updateCalibrationDate',
  UPDATE_FLOW_CALIBRATION_FACTOR = 'updateFlowCalibrationFactor',
  UPDATE_USER_CAL = 'updateUserCal',
  UPDATE_ERROR_STATE = 'updateErrorState',
  DELETE_DEVICE_BY_ID = 'deleteDeviceById',
  SET_SUBSCRIPTION_METADATA = 'setSubscriptionMetadata',
  SET_IOT_CORE_DATA = 'setIotCoreData',
  DELETE_REMOVED_DEVICES = 'deleteRemovedDevices',
  UPDATE_BATTERY_CHARGING = 'updateBatteryCharging',
  UPDATE_BATTERY_INSTALLED = 'updateBatteryInstalled',
  UPDATE_BATTERY_LOW = 'updateBatteryLow',
  UPDATE_BATTERY_REMAINING = 'updateBatteryRemaining',
  UPDATE_UPLOAD_STATUS = 'updateUploadStatus',
  UPDATE_ALARMS = 'updateAlarms'
}

export enum DEVICE_ACTIONS {
  SET_ALL_DEVICES = 'setAllDevices',
  SET_ALL_USER_DEVICES = 'setAllUserDevices',
  SET_DEVICE = 'setDevice',
  UPDATE_DEVICE_COORDS = 'updateDeviceCoords',
  UPDATE_SENSOR_READINGS = 'updateSensorReadings',
  UPDATE_RSSI = 'updateRSSI',
  UPDATE_CONNECTION_TYPE = 'updateConnectionType',
  UPDATE_EOL = 'updateEoL',
  UPDATE_USER_CALS = 'updateUserCals',
  UPDATE_FIRMWARE = 'updateFirmware',
  UPDATE_MQTT_HOST = 'updateMqttHost',
  UPDATE_AVERAGES = 'updateAverages',
  UPDATE_PERCENT_CHANGED = 'updatePercentChanged',
  UPDATE_IS_PUBLIC = 'updateIsPublic',
  UPDATE_PRIVACY_ZONE = 'updatePrivacyZone',
  UPDATE_LAST_TELEMETRY = 'updateLastTelemetry',
  UPDATE_LAST_STATE = 'updateLastState',
  UPDATE_LAST_TELEMETRY_STUDY_ID = 'updateLastTelemetryStudyId',
  UPDATE_DEVICE_STATUS = 'updateDeviceStatus',
  UPDATE_SENSOR_STATUS = 'updateSensorStatus',
  UPDATE_METADATA = 'updateMetadata',
  DELETE_DEVICE_BY_ID = 'deleteDeviceById',
  GET_TELEMETRY_AVERAGES_AND_UPDATE = 'getTelemetryAveragesAndUpdate',
  GET_PERCENT_CHANGED_AND_UPDATE = 'getPercentChangedAndUpdate',
  GET_LATEST_TELEMETRY_AND_UPDATE = 'getLatestTelemetryAndUpdate',
  GET_LATEST_CURRENT_READINGS_AND_UPDATE = 'getLatestCurrentReadingsAndUpdate',
  UPDATE_LATEST_TELEMETRY_FROM_STREAM = 'updateLatestTelemetryFromStream',
  UPDATE_CURRENT_READINGS = 'updateCurrentReadings',
  GET_DEVICE_DETAILS_AND_UPDATE = 'getDeviceDetailsAndUpdate',
  UPDATE_DEVICE_STATE = 'updateDeviceState'
}

export enum DEVICE_GETTERS {
  GET_ACCOUNT_BY_DEVICE_ID = 'getAccountByDeviceId',
  GET_COORDS_BY_DEVICE_ID = 'getCoordsByDeviceId',
  GET_SENSOR_READINGS_BY_DEVICE_ID = 'getSensorReadingsByDeviceId',
  GET_DEVICE_STATUS_BY_DEVICE_ID = 'getDeviceStatusByDeviceId',
  GET_SENSOR_STATUS_BY_DEVICE_ID = 'getSensorStatusByDeviceId',
  GET_RSSI_BY_DEVICE_ID = 'getRSSIByDeviceId',
  GET_CONNECTION_TYPE_BY_DEVICE_ID = 'getConnectionTypeByDeviceId',
  GET_SPS_EOL_BY_DEVICE_ID = 'getSpsEoLByDeviceId',
  GET_USER_CALS_BY_DEVICE_ID = 'getUserCalsByDeviceId',
  GET_FIRMWARE_BY_DEVICE_ID = 'getFirmwareByDeviceId',
  GET_MQTT_HOST_BY_DEVICE_ID = 'getMqttHostByDeviceId',
  GET_AVERAGES_BY_DEVICE_ID = 'getAveragesByDeviceId',
  GET_PERCENT_CHANGED_BY_DEVICE_ID = 'getPercentChangedByDeviceId',
  GET_IS_PUBLIC_BY_DEVICE_ID = 'getIsPublicByDeviceId',
  GET_PRIVACY_ZONE_BY_DEVICE_ID = 'getPrivacyZoneByDeviceId',
  GET_LAST_CONTACT_BY_DEVICE_ID = 'getLastContactByDeviceId',
  GET_LAST_COMMUNICATION_TIME_BY_DEVICE_ID = 'getLastCommunicationTimeByDeviceId',
  GET_LAST_TELEMETRY_TIMESTAMP_BY_DEVICE_ID = 'getLastTelemetryTimestampByDeviceId',
  GET_LAST_TELEMETRY_STUDY_ID_BY_DEVICE_ID = 'getLastTelemetryStudyIdByDeviceId',
  GET_IS_INDOOR_BY_DEVICE_ID = 'getIsIndoorByDeviceId',
  GET_IS_SHARED_BY_DEVICE_ID = 'getIsSharedByDeviceId',
  GET_ZERO_APPLIED_AT_BY_DEVICE_ID = 'getZeroAppliedAtByDeviceId',
  GET_ZERO_STATE_BY_DEVICE_ID = 'getZeroStateByDeviceId',
  GET_NAME_BY_DEVICE_ID = 'getNameByDeviceId',
  GET_LOCATION_NAME_BY_DEVICE_ID = 'getLocationNameByDeviceId',
  GET_MODEL_BY_DEVICE_ID = 'getModelByDeviceId',
  GET_SUBMODEL_BY_DEVICE_ID = 'getSubmodelByDeviceId',
  GET_DEVICE_TYPE_BY_DEVICE_ID = 'getDeviceTypeByDeviceId',
  GET_SERIAL_BY_DEVICE_ID = 'getSerialByDeviceId',
  GET_MEASUREMENTS_BY_DEVICE_ID = 'getMeasurementsByDeviceId',
  GET_SUBSCRIPTION_METADATA_BY_DEVICE_ID = 'getSubscriptionMetadataByDeviceId',
  GET_SUBSCRIPTION_TYPE_BY_DEVICE_ID = 'getSubscriptionTypeByDeviceId',
  GET_WEBSITE_SUBSCRIPTION_NAME = 'getWebsiteSubscriptionName',
  GET_WEBSITE_SUBSCRIPTION_SUB_TYPE = 'getWebsiteSubscriptionSubType',
  GET_PERMISSIONS_BY_DEVICE_ID = 'getPermissionsByDeviceId',
  GET_DEVICE_ID_BY_DEVICE_SERIAL = 'getDeviceIdByDeviceSerial',
  GET_CONNECTED_SMART_BRIDGE_MODEL_BY_DEVICE_ID = 'getConnectedSmartBridgeModelByDeviceId',
  GET_CONNECTED_SMART_BRIDGE_SERIAL_BY_DEVICE_ID = 'getConnectedSmartBridgeSerialByDeviceId',
  GET_CONNECTED_SMART_BRIDGE_ID_BY_DEVICE_ID = 'getConnectedSmartBridgeIdByDeviceId',
  GET_SMART_BRIDGE_CONNECTED_DEVICE_IDS_BY_DEVICE_ID = 'getSmartBridgeConnectedDeviceIdsByDeviceId',
  GET_IS_CONNECTED_BY_DEVICE_ID = 'getIsConnectedByDeviceId',
  GET_IS_TELEMETRY_24HRS_ACTIVE_BY_DEVICE_ID = 'getIsTelemetry24HrsActiveByDeviceId',
  GET_TELEM_TX_TYPE_BY_DEVICE_ID = 'getTelemTxTypeByDeviceId',
  GET_IS_OFFLINE_PRESENT_BY_DEVICE_ID = 'getIsOfflinePresentByDeviceId',
  GET_IS_AUTO_UPLOAD_BY_DEVICE_ID = 'getIsAutoUploadByDeviceId',
  GET_IS_LIVE_PRESENT_BY_DEVICE_ID = 'getIsLivePresentByDeviceId',
  GET_IS_BATTERY_CHARGING_BY_DEVICE_ID = 'getIsBatteryChargingByDeviceId',
  GET_IS_BATTERY_INSTALLED_BY_DEVICE_ID = 'getIsBatteryInstalledByDeviceId',
  GET_IS_BATTERY_LOW_BY_DEVICE_ID = 'getIsBatteryLowByDeviceId',
  GET_BATTERY_REMAINING_BY_DEVICE_ID = 'getBatteryRemainingByDeviceId',
  GET_CALIBRATION_DATE_BY_DEVICE_ID = 'getCalibrationDateByDeviceId',
  GET_FLOW_CALIBRATION_FACTOR_BY_DEVICE_ID = 'getFlowCalibrationFactorByDeviceId',
  GET_CALIBRATION_NAME_BY_DEVICE_ID = 'getCalibrationNameByDeviceId',
  GET_PHOTOMETRIC_CAL_FACTOR_BY_DEVICE_ID = 'getPhotometricCalFactorByDeviceId',
  GET_SIZE_CAL_FACTOR_BY_DEVICE_ID = 'getSizeCalFactorByDeviceId',
  GET_ERROR_STATE_BY_DEVICE_ID = 'getErrorStateByDeviceId',
  GET_IS_THERE_A_VALUE = 'getIsThereAValue',
  GET_DEVICE_ALARMS_BY_DEVICE_ID = 'getDeviceAlarmsByDeviceId',
  GET_DEVICES_WITH_CONNECTED_AFTER_START_ERROR = 'getDevicesWithConnectedAfterStartError',
  GET_DEVICES_WITH_INCOMPLETE_DATA_ERROR = 'getDevicesWithIncompleteDataError',
  GET_ALL_DEVICES_STUDY_ID = 'getAllDevicesStudyId',
  IS_DEVICE_CONNECTED_TO_SMART_BRIDGE = 'isDeviceConnectedToSmartBridge',
  HAS_LATEST_STUDY_BY_DEVICE_ID = 'hasLatestStudyByDeviceId',
  GET_LATEST_STUDY_BY_DEVICE_ID = 'getLatestStudyByDeviceId',
  GET_LATEST_STUDY_MIN_TIMESTAMP_BY_DEVICE_ID = 'getLatestStudyMinTimestampByDeviceId',
  GET_LATEST_STUDY_MAX_TIMESTAMP_BY_DEVICE_ID = 'getLatestStudyMaxTimestampByDeviceId',
  GET_LATEST_STUDY_DURATION_BY_DEVICE_ID = 'getLatestStudyDurationByDeviceId',
  GET_LATEST_STUDY_MEASUREMENT_STATISTICS_BY_DEVICE_ID = 'getLatestStudyMeasurementStatisticsByDeviceId',
  IS_LATEST_DEVICE_STUDY_CONNECTED_AFTER_START = 'isLatestDeviceStudyConnectedAfterStart',
  IS_LATEST_DEVICE_STUDY_COMPLETE = 'isLatestDeviceStudyComplete',
  IS_LATEST_DEVICE_STUDY_INCOMPLETE = 'isLatestDeviceStudyIncomplete',
  IS_LATEST_DEVICE_STUDY_IN_PROGRESS = 'isLatestDeviceStudyInProgress',
  IS_LATEST_DEVICE_STUDY_PENDING = 'isLatestDeviceStudyPending',
  GET_LATEST_STUDY_SAMPLING_INTERVAL_BY_DEVICE_ID = 'getLatestStudySamplingIntervalByDeviceId',
  GET_LATEST_STUDY_START_TIME_BY_DEVICE_ID = 'getLatestStudyStartTimeByDeviceId'
}
